import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ReactComponent as ArrowLeft } from '../../../assets/icon/arrow-left.svg';
import Button from '../../../components/Button';
import AgeVerificationDialog from '../../../components/AgeVerificationDialog/AgeVerificationDialog';
import AgeVerificationRejectedDialog from '../../../components/AgeVerificationDialog/AgeVerificationRejectedDialog';
import { useUserMenuContext } from '../../../context/UserMenuContext';
import { useSystemContext } from '../../../context/SystemContext/SystemContext';
import useAgeVerificationGate from '../../../hook/useAgeVerificationGate';
import { FoodDetailItem, FoodItem } from '../../../interfaces/types';
import { formatPriceItem } from '../../../utils';
import { isPreOrderDisabled } from '../../../utils/preOrderDisabled';
import ItemCounter from './ItemCouter';

interface CustomizeDishProps {
  toggleDrawer: (newOpen: boolean) => () => void;
  onSubmit: SubmitHandler<FormData>;
  dishData: any;
  restaurantFoodMap?: { [key: string]: any };
  onRecommendedFoodClick?: (food: FoodDetailItem, categoryId: string) => void;
  orderDetails?: { foods: FoodItem[] };
  onDrawerClose?: () => void;
  enableAgeVerification?: boolean;
}

const schema = yup.object().shape({
  chooseToppings: yup.array().of(yup.string()),
  note: yup.string(),
});

const CustomizeDish: React.FC<CustomizeDishProps> = ({
  toggleDrawer,
  onSubmit,
  dishData,
  restaurantFoodMap,
  onRecommendedFoodClick,
  orderDetails,
  onDrawerClose,
  enableAgeVerification = false,
}) => {
  const [charCount, setCharCount] = useState(0);
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedToppings, setSelectedToppings] = useState<string[]>([]);
  const [priceFood, setPriceFood] = useState(0);
  const [recommendedFoods, setRecommendedFoods] = useState<FoodDetailItem[]>([]);
  const [recommendedSelections, setRecommendedSelections] = useState<Record<string, number>>({});
  const [maxQuantityDialog, setMaxQuantityDialog] = useState<{ open: boolean; itemName: string; maxQty: number }>({
    open: false,
    itemName: '',
    maxQty: 0,
  });

  const recommendedTotalPrice = Object.entries(recommendedSelections).reduce((sum, [foodId, qty]) => {
    const food = recommendedFoods.find((f) => f.id === foodId);
    return sum + Number(food?.price || 0) * qty;
  }, 0);

  const totalPrice = Number((priceFood * quantity).toFixed(2));
  const grandTotal = Number((totalPrice + recommendedTotalPrice).toFixed(2));
  const { t } = useTranslation();

  const { homeMenuCategories, listRestaurantData } = useUserMenuContext();
  const { isPreOrder, seatInfo } = useSystemContext();
  const {
    openAgeVerification,
    openAgeRejected,
    runWithAgeVerification,
    handleConfirmAgeVerification,
    handleCancelAgeVerification,
    handleCloseAgeVerification,
    handleCloseAgeRejected,
  } = useAgeVerificationGate();
  const orderDate = seatInfo?.order_date ?? null;

  const categoryTitle = homeMenuCategories.find((c) => c.id === dishData.categories?.[0])?.title ?? '';

  const getCartQuantity = (foodId: string): number => {
    if (!orderDetails) return 0;
    // Match by exact id OR by food_id (master id) to handle variants
    const masterId = dishData?.food_id || foodId;
    const totalQuantity = orderDetails.foods.reduce((total, item) => {
      if (item.item_food_id === foodId || item.item_food_id === masterId) {
        return total + item.item_quantity;
      }
      return total;
    }, 0);
    return totalQuantity;
  };

  /** Current in cart for a recommended food (by master id). */
  const getRecommendedFoodCartQty = (food: FoodDetailItem): number => {
    if (!orderDetails) return 0;
    const masterId = food.food_id || food.id;
    return orderDetails.foods.reduce((total, item) => {
      if (item.item_food_id === food.id || item.item_food_id === masterId) return total + item.item_quantity;
      return total;
    }, 0);
  };

  /** Remaining pre-order quantity for a food (pre-order only). */
  const getRemainingPreOrderQty = (food: FoodDetailItem): number | null => {
    if (!isPreOrder || !orderDate || food.pre_order_quantity == null || food.pre_order_quantity <= 0) return null;
    const dateKey = orderDate.slice(0, 10);
    const soldToday = (food.pre_order_sold ?? {})[dateKey] ?? 0;
    return Math.max(0, Number(food.pre_order_quantity) - soldToday);
  };

  const handleMainQuantityChange = (newCount: number) => {
    const nextQuantity = Math.max(1, newCount);
    const remaining = getRemainingPreOrderQty(dishData);
    if (remaining !== null && nextQuantity > remaining) {
      setMaxQuantityDialog({
        open: true,
        itemName: dishData.name,
        maxQty: remaining,
      });
      return;
    }
    setQuantity(nextQuantity);
  };

  const handleCloseMaxQuantityDialog = () => {
    setMaxQuantityDialog({ open: false, itemName: '', maxQty: 0 });
  };

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      note: '',
      chooseToppings: dishData.food ? dishData.food.item_customize_settings.map((setting: any) => setting.name) : [],
    },
  });

  const submitCustomDish = (data: any) => {
    const selectedToppingsData = data.chooseToppings.map((toppingName: string) => {
      const selectedTopping = dishData.customize_group_settings.find((option: any) => option.name === toppingName);
      return { name: selectedTopping.name, price: selectedTopping.price };
    });

    const newData = {
      ...data,
      chooseToppings: selectedToppingsData,
      id: dishData.id,
      category_id: dishData.food ? dishData.food.item_category_id : dishData.categories[0],
      quantity: quantity,
      name: dishData.name,
      price: priceFood,
      total_price: totalPrice,
      sku: dishData.sku ?? '',
      tax_rate: dishData.tax_rate ?? '0',
      ...(enableAgeVerification && { is_alcoholic: dishData.is_alcoholic || false }),
    };
    const recommendedItems: any[] = [];

    Object.entries(recommendedSelections).forEach(([foodId, qty]) => {
      if (qty <= 0) return;
      const recFood = recommendedFoods.find((f) => f.id === foodId);
      if (!recFood) return;
      const restaurant = listRestaurantData?.find((r) => r.id === recFood.restaurantId);
      const preOrderStatus = isPreOrderDisabled(recFood, restaurant, orderDate, isPreOrder);
      if (preOrderStatus.disabled) return;
      const remaining = getRemainingPreOrderQty(recFood);
      if (remaining !== null) {
        const inCart = getRecommendedFoodCartQty(recFood);
        if (inCart + qty > remaining) return;
      }
      recommendedItems.push({
        id: foodId,
        category_id: recFood.categories?.[0] || dishData.categoryId || dishData.categories?.[0],
        quantity: qty,
        name: recFood.name,
        price: recFood.price,
        total_price: Number(recFood.price) * qty,
        sku: recFood.sku ?? '',
        tax_rate: recFood.tax_rate ?? '0',
        restaurantId: recFood.restaurantId,
        ...(enableAgeVerification && { is_alcoholic: recFood.is_alcoholic || false }),
        chooseToppings: [],
        note: '',
      } as any);
    });

    onSubmit({ ...newData, recommendedItems });
  };

  const handleCustomDish = (data: any) => {
    const isAddingAlcoholicRecommendedFood = Object.entries(recommendedSelections).some(([foodId, qty]) => {
      const food = recommendedFoods.find((item) => item.id === foodId);
      return !!food?.is_alcoholic && qty > getRecommendedFoodCartQty(food);
    });

    runWithAgeVerification(enableAgeVerification && (!!dishData.is_alcoholic || isAddingAlcoholicRecommendedFood), () => submitCustomDish(data));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    setCharCount(inputValue.length);
    if (inputValue.length > 200) {
      event.target.value = inputValue.slice(0, 200);
      setCharCount(200);
    }
  };

  const handleToppingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    let updatedToppings = [...selectedToppings];

    if (event.target.checked) {
      updatedToppings = [...selectedToppings, value];
    } else {
      updatedToppings = selectedToppings.filter((topping) => topping !== value);
    }

    setSelectedToppings(updatedToppings);
    setValue('chooseToppings', updatedToppings);
  };

  useEffect(() => {
    let totalToppingsPrice = 0;
    selectedToppings.forEach((toppingName) => {
      const selectedTopping = dishData.customize_group_settings.find((option: any) => option.name === toppingName);
      if (selectedTopping && typeof selectedTopping.price === 'string') {
        totalToppingsPrice += parseFloat(selectedTopping.price);
      } else {
        totalToppingsPrice += selectedTopping.price;
      }
    });

    if (dishData.price && typeof dishData.price === 'string') {
      const newprice = parseFloat(dishData.price);
      const newTotalPrice = newprice + totalToppingsPrice;
      setPriceFood(newTotalPrice);
    } else {
      const newTotalPrice = dishData.price + totalToppingsPrice;
      setPriceFood(newTotalPrice);
    }
  }, [selectedToppings, dishData.price, dishData.customize_group_settings]);

  useEffect(() => {
    const totalQuantity = getCartQuantity(dishData.id);

    if (dishData.food) {
      const selectedToppings = dishData.food.item_customize_settings.map((setting: any) => setting.name);
      setSelectedToppings(selectedToppings);
      setValue('note', dishData.food.item_note ?? '');
      setQuantity(totalQuantity > 0 ? totalQuantity : dishData.food.item_quantity);
    } else {
      if (totalQuantity > 0) {
        setQuantity(totalQuantity);
      }
    }
  }, [dishData]);

  // Get Food Truck recommended foods from dishData (exclude pre-order disabled items)
  useEffect(() => {
    if (dishData?.foodtruck_recommended_food && dishData.foodtruck_recommended_food.length > 0) {
      const recommendedFoodItemsWithOrder: Array<{ food: FoodDetailItem; order: number }> = [];

      dishData.foodtruck_recommended_food.forEach((rf: any) => {
        if (rf.enabled && rf.recommended_food_id) {
          const allFoods = Object.values(restaurantFoodMap ?? {}).flat() as FoodDetailItem[];
          const foodItem = allFoods.find((food: FoodDetailItem) => food.id === rf.recommended_food_id || food.food_id === rf.recommended_food_id);
          if (foodItem && foodItem.enabled) {
            if (isPreOrder && !foodItem.enabled_pre_order) return;

            const restaurant = listRestaurantData?.find((item) => item.id === foodItem.restaurantId);
            const preOrderStatus = isPreOrderDisabled(foodItem, restaurant, orderDate, isPreOrder);
            if (preOrderStatus.disabled) return;
            recommendedFoodItemsWithOrder.push({
              food: foodItem,
              order: typeof rf.order === 'number' ? rf.order : 0,
            });
          }
        }
      });

      recommendedFoodItemsWithOrder.sort((a, b) => a.order - b.order);
      const recommendedFoodItems = recommendedFoodItemsWithOrder.map((item) => item.food);
      setRecommendedFoods(recommendedFoodItems);

      // Sync quantities from cart
      const initialSelections: Record<string, number> = {};
      recommendedFoodItems.forEach((food) => {
        const cartQty =
          orderDetails?.foods.reduce((total, item) => {
            if (item.item_food_id === food.id || item.item_food_id === food.food_id) {
              return total + item.item_quantity;
            }
            return total;
          }, 0) ?? 0;
        if (cartQty > 0) initialSelections[food.id] = cartQty;
      });
      setRecommendedSelections(initialSelections);
    } else {
      setRecommendedFoods([]);
      setRecommendedSelections({});
    }
  }, [restaurantFoodMap, dishData?.foodtruck_recommended_food, isPreOrder, orderDate, orderDetails, listRestaurantData]);

  return (
    <div className="w-[100vw] h-[full] bg-black-0">
      <div className="max-w-[672px] h-[100dvh] mx-auto bg-white flex flex-col relative">
        <div className="flex w-full justify-center top-0 bg-white gap-2 items-center px-4 border-b">
          <button
            className="absolute left-3 p-3"
            onClick={() => {
              toggleDrawer(false)();
              onDrawerClose?.();
            }}
          >
            <ArrowLeft />
          </button>
          <p className="text-gray-700 text-base font-medium leading-6 p-3">{t('DISH_TITLE')}</p>
        </div>

        <form className="w-full h-[calc(100dvh-50px)] flex-1 flex flex-col" onSubmit={handleSubmit(handleCustomDish)}>
          <div className="w-full overflow-auto flex-1">
            <div className="p-4 flex flex-col gap-4">
              <div className="flex gap-4">
                <img className="rounded-lg w-[80px] h-[80px] object-cover" src={dishData.image_url} alt={dishData.name} />
                <div className="flex flex-col">
                  <p className="text-black-800 not-italic text-base leading-6 font-semibold">{dishData.name}</p>
                  <div className="flex gap-1 text-sm leading-5">
                    <p className="text-green-500 font-medium">¥ {formatPriceItem(dishData.price)}</p>
                    <p className="text-black-400 font-light">{t('TAX_INCLUDED')}</p>
                  </div>
                </div>
              </div>
              <p className="text-black-400 not-italic font-normal text-xs leading-[150%] whitespace-pre-line">{dishData.description}</p>
            </div>

            <div className="w-full flex flex-col gap-2">
              <div>
                {dishData.customize_group_settings.length > 0 && (
                  <div className="py-4 border-b">
                    <div className="px-4">
                      <label className="text-gray-800 text-base font-semibold leading-6">{t('DISH_TOPPING')}</label>
                      <div className="flex gap-2 text-xs font-normal leading-[18px] items-center">
                        <p className="text-green-500">{t('DISH_TOPPING_OPTIONAL')}</p>
                        <p className="text-neutral-300">|</p>
                        <p className={errors.chooseToppings?.message ? 'text-red-500' : 'text-black-500'}>{t('DISH_TOPPING_SELECT')}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex flex-col px-4">
                  {dishData &&
                    dishData.customize_group_settings.map((option: any, index: any) => (
                      <div key={index} className="flex justify-between border-b gap-2 items-center py-4">
                        <label htmlFor={`option_${index}`} className="flex justify-between w-full items-center text-sm not-italic leading-5">
                          <p className="text-gray-700 font-normal">{option.name}</p>
                          <p>¥ {formatPriceItem(option.price)}</p>
                        </label>
                        <input
                          className="w-4 h-4"
                          type="checkbox"
                          id={`option_${index}`}
                          value={option.name}
                          {...register(`chooseToppings`)}
                          checked={selectedToppings.includes(option.name)}
                          onChange={handleToppingsChange}
                        />
                      </div>
                    ))}
                </div>
              </div>

              {dishData.enabled_note && (
                <div className="flex flex-col pt-2">
                  <div className="px-4 py-3">
                    <p className="text-gray-800 text-base font-semibold leading-6">{t('MENU_BUTTON_NOTE')}</p>
                    <p className="text-green-500 text-xs">{t('DISH_TOPPING_OPTIONAL')}</p>
                  </div>
                  <div className="p-4">
                    <textarea
                      className="border w-full rounded-lg border-neutral-300 h-[150px] py-3 px-4 focus:border-green-500 focus:outline-none active:border-green-500"
                      placeholder={t('DISH_NOTE_PLACEHOLDER')}
                      {...register('note')}
                      onChange={handleInputChange}
                    ></textarea>
                    <p className="text-black-600 text-sm font-normal leading-5">{charCount}/200</p>
                  </div>
                </div>
              )}

              {/* Recommended Foods Section */}
              {recommendedFoods.length > 0 && (
                <div className="flex flex-col pt-2 pb-[120px]">
                  <div className="px-4 py-3">
                    <p className="text-gray-800 text-base font-semibold leading-6">{t('COMPLEMENT_THIS', { category: categoryTitle })}</p>
                  </div>
                  <div className="px-4 pb-4">
                    <div className="flex gap-4 overflow-x-auto no-scrollbar">
                      {recommendedFoods.map((food: FoodDetailItem) => {
                        const qty = recommendedSelections[food.id] || 0;
                        const restaurant = listRestaurantData?.find((r) => r.id === food.restaurantId);
                        const preOrderStatus = isPreOrderDisabled(food, restaurant, orderDate, isPreOrder);
                        const remaining = getRemainingPreOrderQty(food);
                        const inCart = getRecommendedFoodCartQty(food);
                        const canAddMore = !preOrderStatus.disabled && (remaining === null || inCart + qty + 1 <= remaining);

                        return (
                          <div
                            key={food.id}
                            className={`flex-shrink-0 w-[140px] ${preOrderStatus.disabled ? 'opacity-60 pointer-events-none' : ''}`}
                          >
                            <img className="rounded-lg w-full h-[140px] object-cover mb-2" src={food.image_url} alt={food.name} />
                            <p className="text-gray-700 text-md font-medium mb-1 truncate">{food.name}</p>
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-green-500 text-md font-medium whitespace-nowrap">¥ {formatPriceItem(food.price)}</p>
                              <div className="flex-shrink-0">
                                {qty > 0 ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRecommendedSelections((prev) => {
                                          const next = { ...prev, [food.id]: prev[food.id] - 1 };
                                          if (next[food.id] <= 0) delete next[food.id];
                                          return next;
                                        });
                                      }}
                                      className="bg-gray-200 text-gray-700 rounded w-6 h-6 flex items-center justify-center text-sm font-semibold"
                                    >
                                      −
                                    </button>
                                    <span className="text-sm font-semibold w-4 text-center">{qty}</span>
                                    <button
                                      type="button"
                                      disabled={!canAddMore}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!canAddMore) return;
                                        runWithAgeVerification(enableAgeVerification && food.is_alcoholic, () => {
                                          setRecommendedSelections((prev) => ({ ...prev, [food.id]: (prev[food.id] || 0) + 1 }));
                                        });
                                      }}
                                      className="bg-green-500 text-white rounded w-6 h-6 flex items-center justify-center hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      +
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={!canAddMore}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!canAddMore) return;
                                      runWithAgeVerification(enableAgeVerification && food.is_alcoholic, () => {
                                        setRecommendedSelections((prev) => ({ ...prev, [food.id]: 1 }));
                                      });
                                    }}
                                    className="bg-green-500 text-white rounded w-6 h-6 flex items-center justify-center hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <span className="text-sm font-semibold">+</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 bottom-0 bg-white w-full max-w-[672px] p-4">
            <div className="flex justify-between">
              <label className="text-base font-semibold leading-6 text-gray-700">{t('DISH_ITEM_QUANTITY')}</label>
              <ItemCounter count={quantity} setCount={handleMainQuantityChange} initialCountItem={1} />
            </div>
            {getCartQuantity(dishData.id) > 0 ? (
              <Button type="submit">{t('DISS_BUTTON_UPDATETOCARD')}</Button>
            ) : (
              <Button type="submit">
                {t('DISS_BUTTON_ADDTOCARD')} ¥ {formatPriceItem(grandTotal)}
              </Button>
            )}
          </div>
        </form>
        <Dialog open={maxQuantityDialog.open} onClose={handleCloseMaxQuantityDialog} sx={{ '& .MuiPaper-root': { borderRadius: '12px', width: '100%' } }}>
          <div className="p-6 flex flex-col gap-3 w-full items-center">
            <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('PRE_ORDER_MAX_QUANTITY') || 'Maximum pre-order quantity reached'}</p>
            <p className="text-gray-500 text-sm text-center">
              {t('PRE_ORDER_MAX_QUANTITY_DESC', {
                food_name: maxQuantityDialog.itemName,
                quantity: maxQuantityDialog.maxQty,
                defaultValue: 'You cannot order more than {{quantity}} items for {{food_name}}.',
              })}
            </p>
            <button
              type="button"
              className="px-18 mt-2 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-primary-green border-primary-green text-white"
              onClick={handleCloseMaxQuantityDialog}
            >
              {t('OK')}
            </button>
          </div>
        </Dialog>
        {enableAgeVerification && (
          <>
            <AgeVerificationDialog
              open={openAgeVerification}
              onClose={handleCloseAgeVerification}
              onCancel={handleCancelAgeVerification}
              onConfirm={handleConfirmAgeVerification}
            />
            <AgeVerificationRejectedDialog open={openAgeRejected} onClose={handleCloseAgeRejected} />
          </>
        )}
      </div>
    </div>
  );
};

export default CustomizeDish;
